import { Capacitor } from '@capacitor/core';
import { triggerHaptic } from './nativeBridge';
import { playNativeNotificationSound, usesNativeNotificationSound } from './notificationSound';

/**
 * Alert for an incoming message: chime plus a light haptic, following the
 * phone's ringer in the apps (see notificationSound.js).
 * @param {{ sound?: boolean }} [options] sound: false for muted conversations
 */
export const notifyIncomingMessage = ({ sound = true } = {}) => {
  if (usesNativeNotificationSound()) {
    if (sound) playNativeNotificationSound('message');
    // Android: the plugin vibrates in vibrate mode and stays still in silent
    // mode. iOS haptics already follow the system haptics setting.
    if (Capacitor.getPlatform() === 'ios') triggerHaptic('light');
    return;
  }
  if (sound) playMessageChime();
  triggerHaptic('light');
};

/**
 * Synthesizes a crisp, modern two-tone notification chime using the Web Audio API.
 * Does not require external audio files or network requests, ensuring instant and reliable playback.
 */
export const playMessageChime = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    // Tone 1: Soft warm bell (D5: 587.33Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(587.33, now);
    osc1.frequency.exponentialRampToValueAtTime(740, now + 0.1);

    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.12, now + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    osc1.connect(gain1);
    gain1.connect(ctx.destination);

    // Tone 2: Crisp chime (A5: 880Hz to D6: 1174.66Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(880, now + 0.08);
    osc2.frequency.exponentialRampToValueAtTime(1174.66, now + 0.2);

    gain2.gain.setValueAtTime(0, now + 0.08);
    gain2.gain.linearRampToValueAtTime(0.16, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.38);

    osc2.connect(gain2);
    gain2.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now + 0.08);
    osc1.stop(now + 0.26);
    osc2.stop(now + 0.4);
  } catch (_) {
    // AudioContext autoplay restrictions or disabled audio
  }
};

/**
 * Requests permission for HTML5 Web Notifications on desktop & mobile web
 * @returns {Promise<boolean>} Whether permission was granted
 */
export const requestWebNotificationPermission = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false;
  }
  if (Notification.permission === 'granted') {
    return true;
  }
  if (Notification.permission === 'denied') {
    return false;
  }
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch (_) {
    return false;
  }
};

/**
 * Dispatches an HTML5 Desktop/Browser Notification if supported and permitted
 * @param {string} title
 * @param {object} options
 * @param {Function} [onClick]
 */
export const showWebNotification = (title, options = {}, onClick = null) => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return null;
  }
  if (Notification.permission !== 'granted') {
    return null;
  }

  try {
    const notif = new Notification(title, {
      icon: '/logo.png',
      badge: '/logo.png',
      ...options,
    });

    if (typeof onClick === 'function') {
      notif.onclick = () => {
        window.focus();
        onClick();
        try {
          notif.close();
        } catch (_) {}
      };
    }

    return notif;
  } catch (err) {
    console.debug('[WebNotification] Failed to show notification:', err.message);
    return null;
  }
};
